/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { resetStillThereTimeout } from '../../store/videoplayer/videoplayer.reducers';
import {
    onKeyUp,
    onKeyDown,
    onKeyLeft,
    onKeyRight,
    onKeyTivo,
    onKeyOk,
    onKeyDigit,
    onKeyInfo,
    onKeyBack,
    onKeyChannelDown,
    onKeyChannelUp,
    onKeyPlay,
    onKeyPause,
    onLongStartDown,
    onLongStartUp,
    onLongStopDown,
    onLongStopUp,
    onKeyDash,
    onLongChannelUpStart,
    onLongChannelDownStart,
    onLongChannelUpStop,
    onLongChannelDownStop,
    onKeyStop,
    onKeyFastForward,
    onKeyRewind,
    onKeyPlayPause,
    onKeyReplay,
    onKeyAdvance,
    onKeyGuide,
    onKeyColorBlue,
    onLongLeftStart,
    onLongRightStart,
    onLongLeftStop,
    onLongRightStop,
    onLongOkStart,
    onKeyExit,
} from '../../store/inputfactory/inputfactory.reducers';
import { Log } from '../../core/logging/Log';
import { onKeyEventAction } from '../../store/inputfactory/inputfactory.actions';
import { Keys } from '../../core/navigation/Keys';
import { resetAntiburnTimeout } from '../../store/antiburnosd/antiburnosd.reducers';
import { VuLogger } from '../../core/logging';
import { disableGhostMode } from '../../store/guide/guidehighlight/guidehighlight.reducers';
import { onKeyEventResetOverlayDialogTimer } from '../../store/overlaydialog/overlaydialog.reducers';
import { overlayDisableGhostMode } from '../../store/overlay/overlayhighlight/overlayhighlight.reducers';

interface CustomKeyPressedEvent {
    type: string;
    code: number;
    key: string;
}

const KeyboardEvents: FunctionComponent = ({ children }): JSX.Element => {
    const TAG = 'KeyboardEvents';
    const dispatch = useDispatch();

    const allowed = [
        Keys.KEYBOARD_KEY_0,
        Keys.KEYBOARD_KEY_1,
        Keys.KEYBOARD_KEY_2,
        Keys.KEYBOARD_KEY_3,
        Keys.KEYBOARD_KEY_4,
        Keys.KEYBOARD_KEY_5,
        Keys.KEYBOARD_KEY_6,
        Keys.KEYBOARD_KEY_7,
        Keys.KEYBOARD_KEY_8,
        Keys.KEYBOARD_KEY_9,
        Keys.KEYBOARD_KEY_ADVANCE,
        Keys.KEYBOARD_KEY_BACK,
        Keys.KEYBOARD_KEY_CHANNEL_DOWN,
        Keys.KEYBOARD_KEY_CHANNEL_UP,
        Keys.KEYBOARD_KEY_COLOR_BLUE,
        Keys.KEYBOARD_KEY_DASH,
        Keys.KEYBOARD_KEY_DOWN,
        Keys.KEYBOARD_KEY_EXIT,
        Keys.KEYBOARD_KEY_FAST_FORWARD,
        Keys.KEYBOARD_KEY_GUIDE,
        Keys.KEYBOARD_KEY_INFO,
        Keys.KEYBOARD_KEY_LEFT,
        Keys.KEYBOARD_KEY_OK,
        Keys.KEYBOARD_KEY_PAUSE,
        Keys.KEYBOARD_KEY_PLAY_PAUSE,
        Keys.KEYBOARD_KEY_PLAY,
        Keys.KEYBOARD_KEY_REPLAY,
        Keys.KEYBOARD_KEY_REWIND,
        Keys.KEYBOARD_KEY_RIGHT,
        Keys.KEYBOARD_KEY_STOP,
        Keys.KEYBOARD_KEY_TIVO,
        Keys.KEYBOARD_KEY_UP,
        Keys.LG_REMOTE_KEY_CHANNEL_DOWN,
        Keys.LG_REMOTE_KEY_CHANNEL_UP,
        Keys.LG_REMOTE_KEY_CODE_COLOR_BLUE,
        Keys.LG_REMOTE_KEY_CODE_EXIT,
        Keys.LG_REMOTE_KEY_CODE_MEDIA_FAST_FORWARD,
        Keys.LG_REMOTE_KEY_CODE_MEDIA_PAUSE,
        Keys.LG_REMOTE_KEY_CODE_MEDIA_PLAY,
        Keys.LG_REMOTE_KEY_CODE_MEDIA_REWIND,
        Keys.LG_REMOTE_KEY_CODE_MEDIA_STOP,
        Keys.LG_REMOTE_KEY_CODE_RETURN,
        Keys.SAMSUNG_REMOTE_KEY_CHANNEL_DOWN,
        Keys.SAMSUNG_REMOTE_KEY_CHANNEL_UP,
        Keys.SAMSUNG_REMOTE_KEY_COLOR_BLUE,
        Keys.SAMSUNG_REMOTE_KEY_EXIT,
        Keys.SAMSUNG_REMOTE_KEY_GUIDE,
        Keys.SAMSUNG_REMOTE_KEY_MEDIA_FAST_FORWARD,
        Keys.SAMSUNG_REMOTE_KEY_MEDIA_INFO,
        Keys.SAMSUNG_REMOTE_KEY_MEDIA_PAUSE,
        Keys.SAMSUNG_REMOTE_KEY_MEDIA_PLAY_PAUSE,
        Keys.SAMSUNG_REMOTE_KEY_MEDIA_PLAY,
        Keys.SAMSUNG_REMOTE_KEY_MEDIA_REWIND,
        Keys.SAMSUNG_REMOTE_KEY_MEDIA_STOP,
        Keys.SAMSUNG_REMOTE_KEY_RETURN,
    ];

    const ignoreKeys = [Keys.LG_MOUSE_MODE_ON, Keys.LG_MOUSE_MODE_OFF];

    const getKeyValueFromEvent = (event: CustomKeyPressedEvent): string | number => {
        return event.key === 'Unidentified' ? event.code : event.key.toLowerCase();
    };

    const isAllowedKeyEvent = (event: CustomKeyPressedEvent): boolean => {
        const key: string | number = getKeyValueFromEvent(event);
        if (allowed.indexOf(key) < 0) {
            return false;
        }
        Log.i(TAG, 'key =', key);
        VuLogger.logKeyEvent(new Keys(event));
        dispatch(onKeyEventAction(event.key.toLowerCase(), event.code));
        return true;
    };

    const isIgnoredKeyEvent = (key: any): boolean => {
        if (ignoreKeys.indexOf(key) < 0) return false;
        return true;
    };

    const longKeyPressedStartHandler = (event: CustomKeyPressedEvent): boolean => {
        if (!isAllowedKeyEvent(event)) return false;
        const key: string | number = getKeyValueFromEvent(event);

        switch (key) {
            case Keys.KEYBOARD_KEY_DOWN:
                dispatch(onLongStartDown());
                break;
            case Keys.KEYBOARD_KEY_UP:
                dispatch(onLongStartUp());
                break;
            case Keys.KEYBOARD_KEY_CHANNEL_UP:
            case Keys.LG_REMOTE_KEY_CHANNEL_UP:
            case Keys.SAMSUNG_REMOTE_KEY_CHANNEL_UP:
                dispatch(onLongChannelUpStart());
                break;
            case Keys.KEYBOARD_KEY_CHANNEL_DOWN:
            case Keys.LG_REMOTE_KEY_CHANNEL_DOWN:
            case Keys.SAMSUNG_REMOTE_KEY_CHANNEL_DOWN:
                dispatch(onLongChannelDownStart());
                break;
            case Keys.KEYBOARD_KEY_LEFT:
                dispatch(onLongLeftStart());
                break;
            case Keys.KEYBOARD_KEY_RIGHT:
                dispatch(onLongRightStart());
                break;
            case Keys.KEYBOARD_KEY_OK:
                dispatch(onLongOkStart());
                break;
            default:
                return false;
        }
        return true;
    };

    const longKeyPressedStopHandler = (event: CustomKeyPressedEvent): boolean => {
        if (!isAllowedKeyEvent(event)) return false;
        const key: string | number = getKeyValueFromEvent(event);

        switch (key) {
            case Keys.KEYBOARD_KEY_DOWN:
                dispatch(onLongStopDown());
                break;
            case Keys.KEYBOARD_KEY_UP:
                dispatch(onLongStopUp());
                break;
            case Keys.KEYBOARD_KEY_CHANNEL_UP:
            case Keys.LG_REMOTE_KEY_CHANNEL_UP:
            case Keys.SAMSUNG_REMOTE_KEY_CHANNEL_UP:
                dispatch(onLongChannelUpStop());
                break;
            case Keys.KEYBOARD_KEY_CHANNEL_DOWN:
            case Keys.LG_REMOTE_KEY_CHANNEL_DOWN:
            case Keys.SAMSUNG_REMOTE_KEY_CHANNEL_DOWN:
                dispatch(onLongChannelDownStop());
                break;
            case Keys.KEYBOARD_KEY_LEFT:
                dispatch(onLongLeftStop());
                break;
            case Keys.KEYBOARD_KEY_RIGHT:
                dispatch(onLongRightStop());
                break;
            default:
                return false;
        }
        return true;
    };

    const navigationHandle = (event: CustomKeyPressedEvent): boolean => {
        if (!isAllowedKeyEvent(event)) return false;
        const key: string | number = getKeyValueFromEvent(event);

        switch (key) {
            case Keys.KEYBOARD_KEY_EXIT:
            case Keys.LG_REMOTE_KEY_CODE_EXIT:
            case Keys.SAMSUNG_REMOTE_KEY_EXIT:
                dispatch(onKeyExit());
                break;
            case Keys.KEYBOARD_KEY_LEFT:
                dispatch(onKeyLeft());
                break;
            case Keys.KEYBOARD_KEY_RIGHT:
                dispatch(onKeyRight());
                break;
            case Keys.KEYBOARD_KEY_DOWN:
                dispatch(onKeyDown());
                break;
            case Keys.KEYBOARD_KEY_UP:
                dispatch(onKeyUp());
                break;
            case Keys.KEYBOARD_KEY_TIVO:
                dispatch(onKeyTivo());
                break;
            case Keys.SAMSUNG_REMOTE_KEY_MEDIA_INFO:
            case Keys.KEYBOARD_KEY_INFO:
                dispatch(onKeyInfo());
                break;
            case Keys.KEYBOARD_KEY_OK:
                dispatch(onKeyOk());
                break;
            case Keys.KEYBOARD_KEY_0:
            case Keys.KEYBOARD_KEY_1:
            case Keys.KEYBOARD_KEY_2:
            case Keys.KEYBOARD_KEY_3:
            case Keys.KEYBOARD_KEY_4:
            case Keys.KEYBOARD_KEY_5:
            case Keys.KEYBOARD_KEY_6:
            case Keys.KEYBOARD_KEY_7:
            case Keys.KEYBOARD_KEY_8:
            case Keys.KEYBOARD_KEY_9:
                dispatch(onKeyDigit());
                break;
            case Keys.KEYBOARD_KEY_DASH:
                dispatch(onKeyDash());
                break;
            case Keys.SAMSUNG_REMOTE_KEY_RETURN:
            case Keys.LG_REMOTE_KEY_CODE_RETURN:
            case Keys.KEYBOARD_KEY_BACK:
                dispatch(onKeyBack());
                break;
            case Keys.KEYBOARD_KEY_CHANNEL_UP:
            case Keys.LG_REMOTE_KEY_CHANNEL_UP:
            case Keys.SAMSUNG_REMOTE_KEY_CHANNEL_UP:
                dispatch(onKeyChannelUp());
                break;
            case Keys.KEYBOARD_KEY_CHANNEL_DOWN:
            case Keys.LG_REMOTE_KEY_CHANNEL_DOWN:
            case Keys.SAMSUNG_REMOTE_KEY_CHANNEL_DOWN:
                dispatch(onKeyChannelDown());
                break;
            case Keys.KEYBOARD_KEY_GUIDE:
            case Keys.SAMSUNG_REMOTE_KEY_GUIDE:
                dispatch(onKeyGuide());
                break;
            case Keys.SAMSUNG_REMOTE_KEY_MEDIA_PLAY:
            case Keys.LG_REMOTE_KEY_CODE_MEDIA_PLAY:
            case Keys.KEYBOARD_KEY_PLAY:
                dispatch(onKeyPlay());
                break;
            case Keys.SAMSUNG_REMOTE_KEY_MEDIA_PAUSE:
            case Keys.LG_REMOTE_KEY_CODE_MEDIA_PAUSE:
            case Keys.KEYBOARD_KEY_PAUSE:
                dispatch(onKeyPause());
                break;
            case Keys.KEYBOARD_KEY_STOP:
            case Keys.LG_REMOTE_KEY_CODE_MEDIA_STOP:
            case Keys.SAMSUNG_REMOTE_KEY_MEDIA_STOP:
                dispatch(onKeyStop());
                break;
            case Keys.KEYBOARD_KEY_FAST_FORWARD:
            case Keys.LG_REMOTE_KEY_CODE_MEDIA_FAST_FORWARD:
            case Keys.SAMSUNG_REMOTE_KEY_MEDIA_FAST_FORWARD:
                dispatch(onKeyFastForward());
                break;
            case Keys.KEYBOARD_KEY_REWIND:
            case Keys.LG_REMOTE_KEY_CODE_MEDIA_REWIND:
            case Keys.SAMSUNG_REMOTE_KEY_MEDIA_REWIND:
                dispatch(onKeyRewind());
                break;
            case Keys.KEYBOARD_KEY_PLAY_PAUSE:
            case Keys.SAMSUNG_REMOTE_KEY_MEDIA_PLAY_PAUSE:
                dispatch(onKeyPlayPause());
                break;
            case Keys.KEYBOARD_KEY_REPLAY:
                dispatch(onKeyReplay());
                break;
            case Keys.KEYBOARD_KEY_ADVANCE:
                dispatch(onKeyAdvance());
                break;
            case Keys.KEYBOARD_KEY_COLOR_BLUE:
            case Keys.LG_REMOTE_KEY_CODE_COLOR_BLUE:
            case Keys.SAMSUNG_REMOTE_KEY_COLOR_BLUE:
                dispatch(onKeyColorBlue());
                break;
            default:
                return false;
        }
        return true;
    };

    const customEventHandler = (event: CustomKeyPressedEvent): boolean => {
        switch (event.type) {
            case 'keypressed':
                navigationHandle(event);
                break;
            case 'longkeypressedstart':
                longKeyPressedStartHandler(event);
                break;
            case 'longkeypressedstop':
                longKeyPressedStopHandler(event);
                break;
            default:
                return false;
        }
        dispatch(resetStillThereTimeout());
        dispatch(resetAntiburnTimeout());
        dispatch(onKeyEventResetOverlayDialogTimer());
        dispatch(disableGhostMode());
        dispatch(overlayDisableGhostMode());

        return true;
    };

    /**
     * Implementation for key events such as 'longkeypressedstart', 'longkeypressedstop', and
     * 'keypressed'.
     */
    let longKeyPressTimer: any = null;
    let longKeyPressedCode: any = null;
    let longKeyPressedKey: any = null;
    let wasLongKeyPressEvent = false;
    const LONG_KEY_PRESS_THRESHOLD_TIME = 0.5 * 1000;

    const dispatchLongKeyPressEventStart = (options) => {
        wasLongKeyPressEvent = true;
        longKeyPressedCode = options.keyCode;
        longKeyPressedKey = options.key;
        postMessage(
            { type: 'longkeypressedstart', key: options.key, code: options.keyCode },
            window.location.origin,
        );
    };

    const dispatchLongKeyPressEventStop = (options) => {
        postMessage(
            { type: 'longkeypressedstop', key: options.key, code: options.keyCode },
            window.location.origin,
        );
        longKeyPressedCode = null;
        longKeyPressedKey = null;
        wasLongKeyPressEvent = false;
    };

    const dispatchShortKeyPressEvent = (options) => {
        postMessage(
            { type: 'keypressed', key: options.key, code: options.keyCode },
            window.location.origin,
        );
    };

    const keydownEventHandler = (event) => {
        if (isIgnoredKeyEvent(event.keyCode)) return;
        if (longKeyPressTimer) return;
        if (!longKeyPressTimer)
            longKeyPressTimer = setTimeout(() => {
                dispatchLongKeyPressEventStart({ key: event.key, keyCode: event.keyCode });
            }, LONG_KEY_PRESS_THRESHOLD_TIME);
    };

    const keyupEventHandler = (event) => {
        if (longKeyPressTimer) {
            clearTimeout(longKeyPressTimer);
            longKeyPressTimer = null;
        }
        if (wasLongKeyPressEvent) {
            dispatchLongKeyPressEventStop({
                key: longKeyPressedKey,
                keyCode: longKeyPressedCode,
            });
            return;
        }
        dispatchShortKeyPressEvent({ key: event.key, keyCode: event.keyCode });
    };

    useEffect(() => {
        window.addEventListener('keydown', keydownEventHandler);
        window.addEventListener('keyup', keyupEventHandler);

        window.addEventListener('message', (event: any) => {
            if (!event.data.type) return;
            const data: CustomKeyPressedEvent = {
                type: event.data.type,
                key: event.data.key,
                code: event.data.code,
            };
            customEventHandler(data);
        });

        return () => {
            window.removeEventListener('keydown', () => {});
            window.removeEventListener('keyup', () => {});
            window.removeEventListener('message', () => {});
        };
    }, []);

    return <div>{children}</div>;
};

KeyboardEvents.propTypes = {
    children: PropTypes.node.isRequired,
};

export default KeyboardEvents;
